import { createElement } from 'react';

const TAGS = new Set([
  'p', 'br', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'strong', 'b', 'em', 'i', 'u', 's',
  'ul', 'ol', 'li', 'blockquote', 'hr', 'code', 'pre', 'a',
]);
const DISCARD = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button',
  'svg', 'math', 'img', 'video', 'audio', 'canvas', 'template',
]);

function safeHref(href) {
  try {
    const url = new URL(href);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
  } catch {
    return null;
  }
}

function renderUrls(text, key, insideLink) {
  if (insideLink) return text;

  const matches = [...text.matchAll(/\bhttps?:\/\/[^\s<>"']+/gi)];
  if (matches.length === 0) return text;

  const content = [];
  let cursor = 0;

  matches.forEach((match, index) => {
    const raw = match[0];
    const label = raw.replace(/[.,!?;:)\]}]+$/, '');
    const href = safeHref(label);
    if (!href) return;

    content.push(text.slice(cursor, match.index));
    content.push(createElement('a', { key: `${key}-url-${index}`, href, rel: 'noopener noreferrer' }, label));
    content.push(raw.slice(label.length));
    cursor = match.index + raw.length;
  });

  content.push(text.slice(cursor));
  return content;
}

function renderText(text, key, insideLink = false) {
  const segments = text.split(/(\*\*[^*]+\*\*)/g);
  if (segments.length === 1) return renderUrls(text, key, insideLink);

  return segments.map((segment, index) => segment.startsWith('**') && segment.endsWith('**')
    ? createElement('strong', { key: `${key}-${index}` }, renderUrls(segment.slice(2, -2), `${key}-${index}`, insideLink))
    : renderUrls(segment, `${key}-${index}`, insideLink));
}

function renderNode(node, key, insideLink = false) {
  if (node.nodeType === Node.TEXT_NODE) return renderText(node.textContent, key, insideLink);
  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  const tag = node.localName.toLowerCase();
  if (DISCARD.has(tag)) return null;
  if (tag === 'br' || tag === 'hr') return createElement(tag, { key });

  const href = tag === 'a' ? safeHref(node.getAttribute('href') ?? '') : null;
  const children = Array.from(node.childNodes, (child, index) => renderNode(child, `${key}-${index}`, insideLink || Boolean(href)));
  if (!TAGS.has(tag)) return children;

  if (tag === 'a') {
    return href ? createElement('a', { key, href, rel: 'noopener noreferrer' }, children) : children;
  }

  return createElement(tag, { key }, children);
}

function isDashParagraph(node) {
  return node.nodeType === Node.ELEMENT_NODE && node.localName === 'p'
    && node.firstChild?.nodeType === Node.TEXT_NODE
    && /^\s*[-–]\s+/.test(node.firstChild.textContent);
}

function renderDashParagraph(node, key) {
  const children = Array.from(node.childNodes, (child, index) => index === 0
    ? renderText(child.textContent.replace(/^\s*[-–]\s+/, ''), `${key}-0`)
    : renderNode(child, `${key}-${index}`));

  return createElement('li', { key }, children);
}

export function SafeTebexHtml({ html = '', className = '' }) {
  const document = new DOMParser().parseFromString(String(html), 'text/html');
  const content = [];
  let list = [];

  Array.from(document.body.childNodes).forEach((node, index) => {
    if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() === '') return;
    if (node.nodeType === Node.ELEMENT_NODE && node.localName === 'p'
      && node.textContent.trim() === '') return;

    if (isDashParagraph(node)) {
      list.push(renderDashParagraph(node, `item-${index}`));
      return;
    }

    if (list.length > 0) {
      content.push(createElement('ul', { key: `list-${index}` }, list));
      list = [];
    }

    content.push(renderNode(node, `node-${index}`));
  });

  if (list.length > 0) content.push(createElement('ul', { key: 'list-last' }, list));

  return <div className={className}>{content}</div>;
}
